import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Customer, CustomerCreate, CustomerUpdate, COMMUNICATION_CHANNELS } from '../types/customer';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

interface CustomerDialogProps {
  open: boolean;
  customer?: Customer | null;
  onClose: () => void;
  onSave: () => void;
}

const CustomerDialog: React.FC<CustomerDialogProps> = ({
  open,
  customer,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reseller_id: user?.id || 0,
    name: '',
    country_region: '',
    onboarding_date: new Date(),
    platform_objective: '',
    applicable_regulations: '',
    business_owner_name: '',
    business_owner_email: '',
    technical_contact_name: '',
    technical_contact_email: '',
    main_communication_channel: 'email' as 'email' | 'teams' | 'jira',
    support_schedule: '',
    expected_sla: '',
    observations: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (customer) {
      setFormData({
        reseller_id: customer.reseller_id,
        name: customer.name,
        country_region: customer.country_region,
        onboarding_date: new Date(customer.onboarding_date),
        platform_objective: customer.platform_objective || '',
        applicable_regulations: customer.applicable_regulations || '',
        business_owner_name: customer.business_owner_name,
        business_owner_email: customer.business_owner_email,
        technical_contact_name: customer.technical_contact_name,
        technical_contact_email: customer.technical_contact_email,
        main_communication_channel: customer.main_communication_channel,
        support_schedule: customer.support_schedule || '',
        expected_sla: customer.expected_sla || '',
        observations: customer.observations || '',
      });
    } else {
      setFormData({
        reseller_id: user?.id || 0,
        name: '',
        country_region: '',
        onboarding_date: new Date(),
        platform_objective: '',
        applicable_regulations: '',
        business_owner_name: '',
        business_owner_email: '',
        technical_contact_name: '',
        technical_contact_email: '',
        main_communication_channel: 'email',
        support_schedule: '',
        expected_sla: '',
        observations: '',
      });
    }
    setErrors({});
  }, [customer, open]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('customers.validation.nameRequired');
    }

    if (!formData.country_region.trim()) {
      newErrors.country_region = t('customers.validation.countryRequired');
    }

    if (!formData.business_owner_name.trim()) {
      newErrors.business_owner_name = t('customers.validation.businessOwnerNameRequired');
    }

    if (!formData.business_owner_email.trim()) {
      newErrors.business_owner_email = t('customers.validation.businessOwnerEmailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.business_owner_email)) {
      newErrors.business_owner_email = t('customers.validation.invalidEmail');
    }

    if (!formData.technical_contact_name.trim()) {
      newErrors.technical_contact_name = t('customers.validation.technicalContactNameRequired');
    }

    if (!formData.technical_contact_email.trim()) {
      newErrors.technical_contact_email = t('customers.validation.technicalContactEmailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.technical_contact_email)) {
      newErrors.technical_contact_email = t('customers.validation.invalidEmail');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const data = {
        ...formData,
        onboarding_date: formData.onboarding_date.toISOString(),
        // Remove empty strings for optional fields
        platform_objective: formData.platform_objective || undefined,
        applicable_regulations: formData.applicable_regulations || undefined,
        support_schedule: formData.support_schedule || undefined,
        expected_sla: formData.expected_sla || undefined,
        observations: formData.observations || undefined,
      };

      if (customer) {
        const updateData: CustomerUpdate = { ...data };
        await apiService.updateCustomer(customer.id, updateData);
        toast.success(t('customers.customerUpdated'));
      } else {
        const createData: CustomerCreate = data;
        await apiService.createCustomer(createData);
        toast.success(t('customers.customerCreated'));
      }

      onSave();
    } catch (error: any) {
      console.error('Error saving customer:', error);
      const errorMessage = error.response?.data?.detail || t('customers.saveCustomerError');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
        <DialogTitle>
          {customer ? t('customers.editCustomer') : t('customers.newCustomer')}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Información Básica */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                {t('customers.sections.basicInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`${t('customers.fields.name')} *`}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`${t('customers.fields.countryRegion')} *`}
                value={formData.country_region}
                onChange={(e) => handleInputChange('country_region', e.target.value)}
                error={!!errors.country_region}
                helperText={errors.country_region}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label={`${t('customers.fields.onboardingDate')} *`}
                value={formData.onboarding_date}
                onChange={(date) => handleInputChange('onboarding_date', date || new Date())}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.onboarding_date,
                    helperText: errors.onboarding_date
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('customers.fields.applicableRegulations')}
                value={formData.applicable_regulations}
                onChange={(e) => handleInputChange('applicable_regulations', e.target.value)}
                placeholder={t('customers.placeholders.regulations')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('customers.fields.platformObjective')}
                value={formData.platform_objective}
                onChange={(e) => handleInputChange('platform_objective', e.target.value)}
                multiline
                rows={2}
                placeholder={t('customers.placeholders.objective')}
              />
            </Grid>

            {/* Contactos */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                {t('customers.sections.contacts')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`${t('customers.fields.businessOwnerName')} *`}
                value={formData.business_owner_name}
                onChange={(e) => handleInputChange('business_owner_name', e.target.value)}
                error={!!errors.business_owner_name}
                helperText={errors.business_owner_name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`${t('customers.fields.businessOwnerEmail')} *`}
                type="email"
                value={formData.business_owner_email}
                onChange={(e) => handleInputChange('business_owner_email', e.target.value)}
                error={!!errors.business_owner_email}
                helperText={errors.business_owner_email}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`${t('customers.fields.technicalContactName')} *`}
                value={formData.technical_contact_name}
                onChange={(e) => handleInputChange('technical_contact_name', e.target.value)}
                error={!!errors.technical_contact_name}
                helperText={errors.technical_contact_name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`${t('customers.fields.technicalContactEmail')} *`}
                type="email"
                value={formData.technical_contact_email}
                onChange={(e) => handleInputChange('technical_contact_email', e.target.value)}
                error={!!errors.technical_contact_email}
                helperText={errors.technical_contact_email}
              />
            </Grid>

            {/* Configuración de Soporte */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                {t('customers.sections.supportConfig')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('customers.fields.mainCommunicationChannel')}</InputLabel>
                <Select
                  value={formData.main_communication_channel}
                  onChange={(e) => handleInputChange('main_communication_channel', e.target.value)}
                  label={t('customers.fields.mainCommunicationChannel')}
                >
                  {COMMUNICATION_CHANNELS.map((channel) => (
                    <MenuItem key={channel.value} value={channel.value}>
                      {channel.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('customers.fields.supportSchedule')}
                value={formData.support_schedule}
                onChange={(e) => handleInputChange('support_schedule', e.target.value)}
                placeholder={t('customers.placeholders.supportSchedule')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('customers.fields.expectedSla')}
                value={formData.expected_sla}
                onChange={(e) => handleInputChange('expected_sla', e.target.value)}
                placeholder={t('customers.placeholders.expectedSla')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('customers.fields.observations')}
                value={formData.observations}
                onChange={(e) => handleInputChange('observations', e.target.value)}
                multiline
                rows={3}
                placeholder={t('customers.placeholders.observations')}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? t('common.loading') : (customer ? t('common.update') : t('common.create'))}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CustomerDialog;